/**
 * Onboarding Form user — Insights tab (role === 'onboarding_form').
 *
 * Backend: GET /api/onboarding-form/insights?{range}
 *
 * 4 clickable KPI cards (Total Assigned / Completed / Pending / Overdue) with
 * hover breakdowns. Clicking a card navigates to the Tasks tab, carrying the
 * active date range and selecting the matching status view, so the card count
 * always equals the table count.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, CheckCircle2, Clock, AlertTriangle, RefreshCw, Loader2 } from 'lucide-react';
import DateRangeFilter, { rangeParams } from '../../components/DateRangeFilter';

const authHeader = () => {
  const t = localStorage.getItem('token') || localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const Card = ({ label, value, Icon, accent, testId, onClick, hover }) => (
  <button type="button" onClick={onClick} title={hover}
    className="text-left bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3 shadow-sm hover:shadow-md hover:border-rose-300 transition w-full group relative"
    data-testid={testId}>
    <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accent.bg}`}><Icon size={20} className={accent.fg} /></div>
    <div className="flex-1 min-w-0">
      <p className="text-[11px] font-medium text-gray-500 truncate">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-0.5" data-testid={`${testId}-value`}>{value}</p>
    </div>
    {hover && <span className="absolute hidden group-hover:block bottom-full left-3 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded whitespace-pre z-10">{hover}</span>}
  </button>
);

const OnboardingFormInsights = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams(rangeParams(range, customFrom, customTo)).toString();
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/onboarding-form/insights?${qs}`, { headers: authHeader() });
      setData(await res.json());
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  }, [range, customFrom, customTo]);

  useEffect(() => { load(); }, [load]);

  const c = data?.cards || {};
  const go = (tab) => {
    const p = new URLSearchParams({ tab, range });
    if (range === 'custom') { if (customFrom) p.set('start', customFrom); if (customTo) p.set('end', customTo); }
    navigate(`/tasks?${p.toString()}`);
  };

  return (
    <div className="space-y-5" data-testid="of-insights">
      <div className="flex items-center justify-between bg-white rounded-xl shadow-sm border border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Master Form Insights</h2>
          <p className="text-sm text-gray-500 mt-0.5">Your Master-Form follow-up metrics. Work on tasks from the <span className="font-medium text-rose-600">Tasks</span> tab.</p>
        </div>
        <button onClick={load} disabled={loading} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50" data-testid="of-insights-refresh">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <DateRangeFilter value={range} onChange={setRange} customFrom={customFrom} customTo={customTo}
        onCustomFrom={setCustomFrom} onCustomTo={setCustomTo} testId="of-insights-date-filter" />

      {loading && !data ? (
        <div className="flex items-center justify-center py-16 text-gray-400"><Loader2 size={24} className="animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="of-insights-cards">
          <Card label="Total Master Forms Assigned" value={c.of_total_assigned ?? 0} Icon={ClipboardList} accent={{ bg: 'bg-rose-50', fg: 'text-rose-600' }} testId="of-card-total" onClick={() => go('all')}
            hover={`Assigned: ${c.assigned ?? 0}\nPending: ${c.pending ?? 0}\nCompleted: ${c.completed ?? 0}`} />
          <Card label="Completed Master Forms" value={c.completed ?? 0} Icon={CheckCircle2} accent={{ bg: 'bg-emerald-50', fg: 'text-emerald-600' }} testId="of-card-completed" onClick={() => go('completed')} />
          <Card label="Pending Master Forms" value={c.of_pending ?? 0} Icon={Clock} accent={{ bg: 'bg-amber-50', fg: 'text-amber-600' }} testId="of-card-pending" onClick={() => go('pending_union')}
            hover={`Assigned: ${c.assigned ?? 0}\nNot Completed: ${c.pending ?? 0}`} />
          <Card label="Overdue Master Forms" value={c.overdue ?? 0} Icon={AlertTriangle} accent={{ bg: 'bg-red-50', fg: 'text-red-600' }} testId="of-card-overdue" onClick={() => go('overdue')}
            hover={`Late: ${c.overdue_late ?? 0}\nCritical: ${c.overdue_critical ?? 0}`} />
        </div>
      )}
    </div>
  );
};

export default OnboardingFormInsights;
